// Dashboard.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './Dashboard.css';
import PromoBanner from '../banners/PromoBanner';
import PromoBannerShimmer from '../banners/PromoBannerShimmer';
import VideoAd from '../ads/VideoAd';
import VideoAdShimmer from '../ads/VideoAdShimmer';
import OutOfStockCard from '../actions/OutOfStockCard';
import OutOfStockCardShimmer from '../actions/OutOfStockCardShimmer';
import PendingOrdersCard from '../actions/PendingOrdersCard';
import PendingOrdersCardShimmer from '../actions/PendingOrdersCardShimmer';
import ActionCard from '../actions/ActionCard';
import ActionCardShimmer from '../actions/ActionCardShimmer';
import Shimmer from '../../common/Shimmer';
import { getBannerData, PromoBannerData, VideoAdData } from '../homePageService';
import { formatLastUpdatedAbsolute, formatLastUpdatedTime } from '../cacheService';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import videoThumbnail1 from '../../../assets/images/png/videoad1.png';
import videoThumbnail2 from '../../../assets/images/png/video2.png';
import { clearCache } from '../cacheService';
import SyncIcon from '@mui/icons-material/Sync';
import { useSelector } from 'react-redux';

const HomePageDashboard = () => {
  const [isLoadingBanners, setIsLoadingBanners] = useState(false);
  const [bannersError, setBannersError] = useState<Error | null>(null);
  const [promoBanners, setPromoBanners] = useState<PromoBannerData[]>([]);
  const [videoAds, setVideoAds] = useState<VideoAdData[]>([]);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(formatLastUpdatedTime());
  const [refreshKey, setRefreshKey] = useState(0);

  // Access portal config state from Redux store
  const portalConfigState = useSelector((state: any) => state.portalConfigState);
  
  // Check if createPrimaryOrder feature is available
  const isCreatePrimaryOrderAvailable = portalConfigState?.featureConfig?.some(
    (feature: any) => feature.name === 'createPrimaryOrder'
  ) || false;

  const fetchBannerData = async () => {
    setIsLoadingBanners(true);
    setBannersError(null);
    try {
      const { promoBanners: fetchedPromoBanners, videoAds: fetchedVideoAds } = await getBannerData();
      setPromoBanners(fetchedPromoBanners);
      setVideoAds(fetchedVideoAds);
    } catch (err) {
      console.error('Error fetching banner data:', err);
      setBannersError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setIsLoadingBanners(false);
    }
  };

  const handleManualRefresh = () => {
    // Clear all homepage caches so all sections refetch fresh data
    clearCache();
    setRefreshKey(prev => prev + 1);
    setLastUpdatedTime(formatLastUpdatedTime());
  };

  // Auto-refresh hook
  useAutoRefresh({
    intervalMs: 900000, // 1 minute
    onRefresh: () => {
      setRefreshKey(prev => prev + 1);
      setLastUpdatedTime(formatLastUpdatedTime());
    }
  });

  useEffect(() => {
    fetchBannerData();
  }, [refreshKey]);

  // Update the last updated time every 30 seconds to show relative time
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdatedTime(formatLastUpdatedTime());
    }, 900000);

    return () => clearInterval(interval);
  }, []);


  
  const userName = (): string => {
    try {
      const authContextString = localStorage.getItem("authContext");
      if (!authContextString) return 'Distributor';
      const authContext = JSON.parse(authContextString);
      const name = authContext?.user?.name;
      return typeof name === 'string' && name.trim().length > 0 ? name : 'Distributor';
    } catch {
      return 'Distributor';
    }
  }
  const truncateString = (str: string, maxLength: number) => {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength) + '...';
  };
  const getCurrentGreeting = () => {
    try {
      const now = typeof window !== 'undefined' ? new Date() : null;
      const hour = now?.getHours();

      if (typeof hour !== 'number' || Number.isNaN(hour) || hour < 0 || hour > 23) {
        return "Hello";
      }

      if (hour < 12) return "Good morning";
      if (hour < 17) return "Good afternoon";
      return "Good evening";
    } catch {
      return "Hello";
    }
  };


  return (
    <div className="dashboard-container-layout">
      <div className="dashboard-greeting">
        {isLoadingBanners ? (
          <Shimmer width="300px" height="32px" />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ margin: '6px 0px' }} title={userName()}>👋 {getCurrentGreeting()} {truncateString(userName(), 80)}</h3>
            {lastUpdatedTime && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', paddingRight: '10px' }}>
                <span style={{ 
                  fontSize: '0.9rem', 
                  color: '#969ea4', 
                  fontWeight: 400,
                  fontStyle: 'italic'
                }}>
                  Last updated on: {formatLastUpdatedAbsolute()}
                </span>
                <SyncIcon
                  sx={{ fill: '#1C7EA0 !important', cursor: 'pointer' }}
                  onClick={handleManualRefresh}
                />
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="dashboard-banners">
        {isLoadingBanners ? (
          <PromoBannerShimmer />
        ) : (
          <PromoBanner banners={promoBanners} isClickable={isCreatePrimaryOrderAvailable} />
        )}
      </div>
      
      <div className="dashboard-pending">
        {isLoadingBanners ? (
          <Shimmer width="250px" height="24px" style={{ marginBottom: '4px' }} />
        ) : (
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px', color:'#464748' }}>Pending actions for today</h3>
        )}
        <div className="dashboard-pending-cards">
          {isLoadingBanners ? (
            <>
              <OutOfStockCardShimmer />
              <PendingOrdersCardShimmer />
              <ActionCardShimmer />
            </>
          ) : (
            <>
              <OutOfStockCard isCreatePrimaryOrderAvailable={isCreatePrimaryOrderAvailable} />
              <PendingOrdersCard />
              <ActionCard />
            </>
          )}
        </div>
      </div>

      <div className="dashboard-video-ads">
        {isLoadingBanners ? (
          <>
            <VideoAdShimmer />
            <VideoAdShimmer />
          </>
        ) : videoAds && videoAds.length > 0 ? (
          videoAds.map((videoAdItem, index) => (
            <VideoAd
              key={videoAdItem?.id || index}
              title={videoAdItem?.title || 'Watch Now'}
              subtitle={videoAdItem?.subtitle || 'Discover amazing content'}
              buttonText={videoAdItem?.buttonText || 'Watch Now'}
              videoThumbnail={index === 0 ? videoThumbnail2 : videoThumbnail1}
              brand={videoAdItem?.brand || 'Brand'}
              logo={index === 0 ? videoThumbnail1 : videoThumbnail2}
              videoUrl={videoAdItem?.videoUrl || 'https://www.youtube.com/watch?v=gdGojS81LPs'}
            />
          ))
        ) : null}
      </div>
    </div>
  );
};

export default HomePageDashboard;